import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState as GS } from '../../stores/types/types';
import { ConnectedUser } from '../../connection/socket';
import { Invitation, Profile } from '../../stores/reducers/invitations';
import { FEED_TYPES, ShotFeed } from '../../stores/reducers/shots';
import { MediaItem } from '../../stores/reducers/media';
import { contrastingColor, getNameOrInitials } from '../../utils/utils';
import Icon, { ICONS, COLORS } from './icon';
import { pickColor } from './avatar';
import Thumbnail from '../chat/chat-document-thumbnail';
import { getCurrentPageThumb } from '../../utils/media';
import { ROOM_STATES } from '../../stores/reducers/settings';
import AudioThumbnail from '../media/audio-thumbnail';
import useProfile from '../../hooks/use-profile';
import { ProfileInterface, SafeProfileInterface } from '../../stores/types/user-profiles';
import { ProfileMask } from '../../stores/types/masks';
import colors from '../../utils/colors';
import { EnablerStates } from '../../stores/types/session';
import EmptyScreenShare from '../main-video/empty-screen-share';

interface Props {
	shotFeed: ShotFeed;
	forShowDetails?: boolean;
}

export default function FeedThumbnail(props: Props) {
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const srtStreams = useSelector((s: GS) => s.VideoReducer.srtStreams);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const presentedMedia = useSelector((s: GS) => s.MediaReducer.presentedMedia);
	const showbuilderMedia = useSelector((s: GS) => s.MediaReducer.showbuilderMedia);
	const homeMeeting = useSelector((s: GS) => s.HomeReducer.homeMeeting);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const isEnabler = useSelector((s: GS) => s.MeetingReducer.enablerState !== EnablerStates.None);

	const {
		shotFeed,
		forShowDetails = false,
	} = props;

	const feedId = useMemo(() => {
		if (shotFeed.feedType === FEED_TYPES.PRESENTER && shotFeed.presenter) {
			return shotFeed.presenter.toString();
		} else if ((shotFeed.feedType === FEED_TYPES.MEDIA || shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO) && shotFeed.media) {
			return shotFeed.media.toString();
		} else if (shotFeed.feedType === FEED_TYPES.EMAIL && shotFeed.email) {
			return shotFeed.email;
		} else if (shotFeed.feedType === FEED_TYPES.SCREENSHARE && shotFeed.screenshare) {
			return shotFeed.screenshare;
		} else if (shotFeed.feedType === FEED_TYPES.SIP_CALL && shotFeed.sipStreamId) {
			return shotFeed.sipStreamId;
		} else if (shotFeed.feedType === FEED_TYPES.SRT_STREAM && shotFeed.srtStreamId) {
			return shotFeed.srtStreamId.toString();
		} else if (shotFeed.feedType === FEED_TYPES.ONSITE_LOCATION && shotFeed.onsiteLocationId) {
			return shotFeed.onsiteLocationId.toString();
		}
		return '';
	}, [shotFeed]);

	const mediaItem = useMemo(
		() => media.find((med: MediaItem) => med.id === Number(feedId)),
		[feedId, media],
	);

	const mediaGeneratedContent = useMemo(() => {
		if (!mediaItem) return null;
		return generatedContent.find((gc) => gc.fkTable === 'gr_media' && gc.fkId === mediaItem.id);
	}, [generatedContent, mediaItem]);

	const presentedMediaItem = useMemo(
		() => (
			roomState === ROOM_STATES.SHOWTIME
				? presentedMedia.find((m) => m.mediaId === Number(feedId))
				: showbuilderMedia.find((m) => m.mediaId === Number(feedId))
		),
		[feedId, presentedMedia, showbuilderMedia, roomState],
	);

	const thumbImage = useMemo(() => {
		// this is a processing capsule item show this thumbnail while it is generating
		if (mediaGeneratedContent && mediaItem?.type === 'hls' && mediaItem?.thumbnail_url) {
			return mediaItem.thumbnail_url;
		}
		return getCurrentPageThumb(mediaItem?.media_data, presentedMediaItem?.currentPage);
	}, [presentedMediaItem?.currentPage, mediaItem?.media_data]);

	function getPosterImage(user: Profile | ConnectedUser) {
		if (user && (user.poster || user.avatar)) {
			return user.poster || user.avatar;
		}
		return '';
	}

	function getBackgroundColor(user: Profile | ConnectedUser) {
		if (!user) return '';

		if (user.email) {
			return pickColor(user.email);
		} if (user.gr_user) {
			return pickColor(user.gr_user.toString());
		}
		return '';
	}

	const {
		getMaskedProfileById,
		getMaskFromEmail,
	} = useProfile({});

	const mask = useMemo(() => {
		let mask: ProfileInterface | ProfileMask | null = getMaskedProfileById(Number(feedId));
		if (!mask) {
			mask = getMaskFromEmail(feedId);
		}
		return mask ? {...mask, poster: mask.bg_image} : {};
	}, [feedId, getMaskedProfileById, getMaskFromEmail]);

	const userFromId = useMemo(() => {
		const connectedUser = connectedUsers.find(
			(user: ConnectedUser) => user.gr_user === Number(feedId),
		);
		if (connectedUser) return {...connectedUser, ...mask};
		const invite = invitations.find(
			(i: Invitation) => i.profile.gr_user === Number(feedId),
		);
		if (invite) {
			return {...invite.profile, ...mask}
		}
		return {
			email: '',
			first_name: '',
			last_name: '',
			avatar: '',
			job_title: '',
			gr_user: 0,
		} as Profile;
	}, [connectedUsers, invitations, feedId, mask]);

	const videoStream = connectedPresenterVideos.find((vid) => vid.isSipCall && vid.id === feedId);
	const sipCallPoster = videoStream?.poster || '';
	const sipCallName = videoStream?.isSipCall && videoStream?.displayName;

	const placeholderStyle = useMemo(() => {
		if (meeting) {
			return {
				backgroundColor: meeting.primary_color
					? `#${meeting.primary_color}`
					: '#333',
				color: contrastingColor(meeting.primary_color || colors.gray33),
			};
		} if (homeMeeting) {
			return {
				backgroundColor: shotFeed.feedType === FEED_TYPES.EMAIL
					? pickColor(feedId)
					: getBackgroundColor(userFromId),
			};
		}
		return {
			backgroundColor: '#333',
			color: contrastingColor(colors.gray33),
		};
	}, [meeting, homeMeeting, shotFeed.feedType, feedId, userFromId]);

	const getUserAvatar = () => (
			<div className="poster active">
				{(feedId && !!getPosterImage(userFromId))
					? <img className="poster-image" src={getPosterImage(userFromId)} alt={''} />
					: <div className="no-image-placeholder">
						<span style={placeholderStyle}>{getNameOrInitials(userFromId, false)}</span>
					</div>
				}
			</div>
	);

	const getSipCallPoster = () => (
		<div className="poster active">
			{(feedId && !!sipCallPoster)
				? <img className="poster-image" src={sipCallPoster} alt={''} />
				: <div className="no-image-placeholder">
					<span style={placeholderStyle}>{sipCallName}</span>
				</div>
			}
		</div>
	);

	const getSrtStreamImage = () => {
		const srtStream = srtStreams.find((stream) => stream.id === Number(feedId));
		if (!srtStream) return <></>;
		const { name, thumbnail, safetyImage } = srtStream;
		return (
			<div className="poster active srt-feed">
				<img className="poster-image" src={thumbnail || safetyImage} alt={name} />
			</div>
		);
	};

	const getOnsiteLocationImage = () => {
		const onsiteLocation = onsiteLocations.find((location) => location.id === Number(feedId));
		if (!onsiteLocation) return <></>;
		const { locationName, safetyImage } = onsiteLocation;
		return (
			<div className="poster active srt-feed">
				<img className="poster-image" src={safetyImage} alt={locationName} />
			</div>
		);
	};

	const getScreensharePlaceholder = () => {
		if (isEnabler) {
			const user = connectedUsers.find((user: ConnectedUser) => user.email === feedId);
			const invite = invitations.find((invitation: Invitation) => invitation.profile.email === feedId);
			return <EmptyScreenShare grUserId={user?.gr_user ?? invite?.profile?.gr_user ?? 0} size={76} />;
		}
		return (
			<div className="empty-video screenshare">
				<div className="placeholder-icon">
					<Icon name={ICONS.SHARE_SCREEN} color={COLORS.WHITE} size={76} />
				</div>
			</div>
		);
	}

	const getFeed = (feedType: string) => {
		switch (feedType) {
			case FEED_TYPES.PRESENTER:
				return getUserAvatar();
			case FEED_TYPES.SIP_CALL:
				return getSipCallPoster();
			case FEED_TYPES.SRT_STREAM:
				return getSrtStreamImage();
			case FEED_TYPES.ONSITE_LOCATION:
				return getOnsiteLocationImage();
			case FEED_TYPES.SCREENSHARE:
				return getScreensharePlaceholder();
			case FEED_TYPES.MEDIA:
				return (
					<div
						className={classNames('media', mediaItem?.media_data.fill && 'fill')}
						style={{ background: mediaItem?.media_data?.background_color }}
					>
						<Thumbnail thumbnail={thumbImage} className='media-img' />
					</div>
				);
			case FEED_TYPES.EMAIL:
				return (
					<div className="poster active">
						<div className="email-feed no-image-placeholder">
							<span style={placeholderStyle}>{feedId.trim()[0]}</span>
						</div>
					</div>
				);
			case FEED_TYPES.MEDIA_AUDIO:
				return (
					<AudioThumbnail />
				);
			default: {
				return (
					<div className="empty-video">
						<div className="placeholder-icon">
							<Icon
								name={ICONS.ADD_INVITE}
								color={COLORS.PLACEHOLDER_GRAY}
								size={isEnabler ? 76 : forShowDetails ? 50 : 25}
							/>
						</div>
					</div>
				);
			}
		}
	};

	return (
		<>
			{getFeed(shotFeed.feedType)}
		</>
	);
}
